import { useEffect, useRef, useState } from 'react'
import type { Notify } from '../../api/types'
import { Icon } from '../../components/Icon'
import { channelDisplayName } from '../../lib/channels'
import { toast } from '../../state/toasts'
import { useConversation } from '../conversation/ConversationContext'
import { muteOptions, mutedUntilFor, FOREVER } from './muteOptions'
import { saveNotify } from './notify'
import { defaultNotify, quietLabel, quietState } from './quiet'
import { formatClock } from '../../lib/format'

/** How long a mute lasts, beside its switch: "Until you unmute", "Until 22:15", or "Until Tue 09:00" past today. */
function muteLength(mutedUntil: string | null): string {
  if (!mutedUntil || mutedUntil === FOREVER) return 'Until you unmute'
  const until = new Date(mutedUntil)
  const sameDay = until.toDateString() === new Date().toDateString()
  return `Until ${sameDay ? formatClock(mutedUntil) : until.toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' }).replace(',', '')}`
}

const notifyOptions: { id: Notify; label: string; help: string }[] = [
  { id: 'all', label: 'All', help: 'Every new message.' },
  { id: 'mentions', label: 'Mentions', help: 'Only when someone @mentions you.' },
  { id: 'none', label: 'Nothing', help: 'Never, until you change this.' },
]

/**
 * Notifications for this conversation. What it is doing now comes first, in a sentence and with the way out beside it:
 * this is the screen somebody reaches when their phone has gone quiet and they do not know why.
 */
export function NotificationSection({ focus }: { focus?: boolean }) {
  const { channel, users, me } = useConversation()
  const quiet = quietState(channel)
  const heading = useRef<HTMLHeadingElement>(null)
  const oneToOne = channel.type === 'dm' || channel.type === 'bot_dm'
  const name = channelDisplayName(channel, users, me.id)

  useEffect(() => {
    if (focus) heading.current?.focus()
  }, [focus])

  const save = (notify: Notify, mutedUntil: string | null) => saveNotify(channel, notify, mutedUntil)
  const muted = quiet?.kind === 'muted'
  // Turned on and not yet told for how long: the lengths show, nothing is muted until one is picked.
  const [choosing, setChoosing] = useState(false)

  const turnOn = async () => {
    const previous = { notify: channel.notify, mutedUntil: channel.mutedUntil }
    const off = quiet?.kind === 'off'
    if (!(await save(off ? defaultNotify(channel) : channel.notify, null))) return
    toast(off ? `Notifications on for ${name}` : `Unmuted ${name}`, {
      action: { label: 'Undo', onClick: () => void save(previous.notify, previous.mutedUntil) },
    })
  }

  return (
    <section className="drawer-section" aria-labelledby="notify-heading">
      <div className="section-heading">
        <h3 id="notify-heading" ref={heading} tabIndex={-1}>
          Notifications
        </h3>
      </div>
      {quiet ? (
        <div className="quiet-status" role="note">
          <Icon name="mute" />
          <span>
            <strong>{quietLabel(quiet, 'short')}</strong>
            <small>{quietLabel(quiet, 'long')}</small>
          </span>
          {/* Muted: the switch below unmutes. Off: this turns them back on. */}
          {quiet.kind === 'off' ? (
            <button type="button" className="button small" onClick={() => void turnOn()}>
              Turn on
            </button>
          ) : null}
        </div>
      ) : null}
      {/* Mute is a switch, as on a phone's own settings: turning it on asks for how long; the choice mutes. */}
      <div className="option-row">
        <span>
          Mute
          {muted ? <small id={`mute-length-${channel.id}`}>{muteLength(channel.mutedUntil)}</small> : null}
        </span>
        <label className="switch">
          <input
            type="checkbox"
            role="switch"
            aria-label="Mute"
            aria-describedby={muted ? `mute-length-${channel.id}` : undefined}
            checked={muted || choosing}
            onChange={(e) => {
              if (e.target.checked) setChoosing(true)
              else if (muted) void turnOn()
              else setChoosing(false)
            }}
          />
          <span />
        </label>
      </div>
      {choosing && !muted ? (
        <div role="radiogroup" aria-label="Mute for" className="choice-list compact">
          {muteOptions.map((o) => (
            <label key={o.id} className="choice">
              <input
                type="radio"
                name={`mute-${channel.id}`}
                checked={false}
                onChange={() => {
                  setChoosing(false)
                  void save(channel.notify, mutedUntilFor(o.id))
                }}
              />
              <span>
                <strong>{o.label}</strong>
              </span>
            </label>
          ))}
        </div>
      ) : null}
      {/* A direct message always notifies unless it is muted, so the standing choice would only contradict itself here. */}
      {oneToOne ? null : (
        <div role="radiogroup" aria-label="Notify me about" className="choice-list">
          <span className="choice-list-label">Notify me about</span>
          {notifyOptions.map((o) => (
            <label key={o.id} className={channel.notify === o.id ? 'choice is-chosen' : 'choice'}>
              <input type="radio" name={`notify-${channel.id}`} checked={channel.notify === o.id} onChange={() => void save(o.id, channel.mutedUntil)} />
              <span>
                <strong>{o.label}</strong>
                <small>{o.help}</small>
              </span>
            </label>
          ))}
        </div>
      )}
    </section>
  )
}
